// Hand entry on the vehicle card: a cost, a service or inspection, an odometer reading, and the
// internal workshop's jobs (answer 17). Each is a small ruled form that opens from a button.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { ErrorSummary, SelectField, TextField } from '../ui/Fields.tsx';
import { Table } from '../ui/Table.tsx';
import { Status } from '../ui/Status.tsx';
import { kr, todayIso } from '../format.ts';

type Job = {
  id: string;
  number: string;
  openedOn: string;
  closedOn: string | null;
  status: string;
  description: string;
  hours: number;
  hourlyRateSek: number;
  partsSek: number;
  mechanic: string | null;
  totalSek: number;
};

function useSave(vehicleId: string, path: string, onDone: () => void) {
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/vehicles/${vehicleId}${path}`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] });
      await qc.invalidateQueries({ queryKey: ['workshop', vehicleId] });
      setErrors({});
      onDone();
    },
    onError: (e) =>
      setErrors(
        e instanceof ApiError && e.errors.length
          ? Object.fromEntries(e.errors.map((x) => [x.path, x.message]))
          : { form: sv.errors.server },
      ),
  });
  return { m, errors };
}

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

export function CostForm({ vehicleId, onDone }: { vehicleId: string; onDone: () => void }) {
  const [d, setD] = useState({
    costDate: todayIso(),
    category: 'bransle',
    amountSek: '',
    supplier: '',
    description: '',
    reference: '',
  });
  const { m, errors } = useSave(vehicleId, '/costs', onDone);
  const set = (k: keyof typeof d) => (v: string) => setD({ ...d, [k]: v });
  return (
    <form
      className="form-block reveal"
      aria-labelledby="cost-form"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate({
          costDate: d.costDate,
          category: d.category,
          amountSek: num(d.amountSek) ?? 0,
          supplier: d.supplier || null,
          description: d.description || null,
          reference: d.reference || null,
        });
      }}
    >
      <h3 className="form-heading" id="cost-form">
        {sv.entry.addCost}
      </h3>
      <ErrorSummary errors={Object.entries(errors).map(([k, v]) => ({ id: k, message: v }))} />
      <TextField
        id="costDate"
        label={sv.entry.costDate}
        type="date"
        value={d.costDate}
        required
        onChange={set('costDate')}
      />
      <SelectField
        id="category"
        label={sv.entry.category}
        value={d.category}
        options={Object.entries(sv.costCategories).map(([value, label]) => ({ value, label }))}
        onChange={set('category')}
      />
      <TextField
        id="amountSek"
        label={sv.entry.amount}
        type="number"
        value={d.amountSek}
        required
        error={errors.amountSek}
        onChange={set('amountSek')}
      />
      <TextField
        id="supplier"
        label={sv.entry.supplier}
        value={d.supplier}
        onChange={set('supplier')}
      />
      <TextField
        id="description"
        label={sv.entry.description}
        value={d.description}
        onChange={set('description')}
      />
      <TextField
        id="reference"
        label={sv.entry.reference}
        value={d.reference}
        onChange={set('reference')}
      />
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={m.isPending || !d.amountSek}>
          {sv.entry.save}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          {sv.entry.cancel}
        </button>
      </div>
    </form>
  );
}

export function ServiceForm({ vehicleId, onDone }: { vehicleId: string; onDone: () => void }) {
  const [d, setD] = useState({
    doneOn: todayIso(),
    kind: 'service',
    odometerKm: '',
    workshop: '',
    description: '',
    costSek: '',
    nextDueOn: '',
    nextDueKm: '',
  });
  const { m, errors } = useSave(vehicleId, '/service', onDone);
  const set = (k: keyof typeof d) => (v: string) => setD({ ...d, [k]: v });
  return (
    <form
      className="form-block reveal"
      aria-labelledby="service-form"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate({
          doneOn: d.doneOn,
          kind: d.kind,
          odometerKm: num(d.odometerKm),
          workshop: d.workshop || null,
          description: d.description || null,
          costSek: num(d.costSek),
          nextDueOn: d.nextDueOn || null,
          nextDueKm: num(d.nextDueKm),
        });
      }}
    >
      <h3 className="form-heading" id="service-form">
        {sv.entry.addService}
      </h3>
      <ErrorSummary errors={Object.entries(errors).map(([k, v]) => ({ id: k, message: v }))} />
      <TextField
        id="doneOn"
        label={sv.entry.doneOn}
        type="date"
        value={d.doneOn}
        required
        onChange={set('doneOn')}
      />
      <SelectField
        id="kind"
        label={sv.entry.kind}
        value={d.kind}
        options={Object.entries(sv.serviceKinds).map(([value, label]) => ({ value, label }))}
        onChange={set('kind')}
      />
      <TextField
        id="odometerKm"
        label={sv.entry.odometerKm}
        type="number"
        value={d.odometerKm}
        onChange={set('odometerKm')}
      />
      <TextField
        id="workshop"
        label={sv.entry.workshop}
        value={d.workshop}
        onChange={set('workshop')}
      />
      <TextField
        id="description"
        label={sv.entry.description}
        value={d.description}
        onChange={set('description')}
      />
      <TextField
        id="costSek"
        label={sv.entry.costSek}
        type="number"
        value={d.costSek}
        onChange={set('costSek')}
      />
      <TextField
        id="nextDueOn"
        label={sv.entry.nextDueOn}
        type="date"
        value={d.nextDueOn}
        onChange={set('nextDueOn')}
      />
      <TextField
        id="nextDueKm"
        label={sv.entry.nextDueKm}
        type="number"
        value={d.nextDueKm}
        onChange={set('nextDueKm')}
      />
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={m.isPending}>
          {sv.entry.save}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          {sv.entry.cancel}
        </button>
      </div>
    </form>
  );
}

export function OdometerForm({ vehicleId, onDone }: { vehicleId: string; onDone: () => void }) {
  const [date, setDate] = useState(todayIso());
  const [km, setKm] = useState('');
  const { m, errors } = useSave(vehicleId, '/odometer', onDone);
  return (
    <form
      className="form-block reveal"
      aria-labelledby="odo-form"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate({ readAt: `${date}T12:00:00+01:00`, km: num(km) ?? 0, source: 'manual' });
      }}
    >
      <h3 className="form-heading" id="odo-form">
        {sv.entry.addOdometer}
      </h3>
      <ErrorSummary errors={Object.entries(errors).map(([k, v]) => ({ id: k, message: v }))} />
      <TextField
        id="readAt"
        label={sv.entry.readAt}
        type="date"
        value={date}
        required
        onChange={setDate}
      />
      <TextField id="km" label={sv.entry.km} type="number" value={km} required onChange={setKm} />
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={m.isPending || !km}>
          {sv.entry.save}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          {sv.entry.cancel}
        </button>
      </div>
    </form>
  );
}

export function WorkshopSection({ vehicleId, canWrite }: { vehicleId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const jobs = useQuery({
    queryKey: ['workshop', vehicleId],
    queryFn: () => api<Job[]>(`/vehicles/${vehicleId}/workshop`),
  });
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({
    openedOn: todayIso(),
    description: '',
    hours: '1',
    hourlyRateSek: '680',
    partsSek: '0',
    mechanic: '',
  });
  const { m, errors } = useSave(vehicleId, '/workshop', () => setOpen(false));
  const [closing, setClosing] = useState<Job | null>(null);
  const [closeHours, setCloseHours] = useState('');
  const [closeParts, setCloseParts] = useState('');
  const close = useMutation({
    mutationFn: () =>
      api(`/vehicles/${vehicleId}/workshop/${closing!.id}/close`, {
        method: 'POST',
        body: JSON.stringify({
          closedOn: todayIso(),
          hours: num(closeHours) ?? 0,
          partsSek: num(closeParts) ?? 0,
        }),
      }),
    onSuccess: async () => {
      setClosing(null);
      await qc.invalidateQueries({ queryKey: ['workshop', vehicleId] });
      await qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] });
    },
  });
  const set = (k: keyof typeof d) => (v: string) => setD({ ...d, [k]: v });
  return (
    <section className="block" aria-labelledby="workshop">
      <h2 className="block-title" id="workshop">
        {sv.entry.workshopJobs}
      </h2>
      <Table
        caption={sv.entry.workshopJobs}
        isEmpty={(jobs.data?.length ?? 0) === 0}
        empty={sv.entry.workshopEmpty}
        hasOptional
      >
        <thead>
          <tr>
            <th scope="col">{sv.entry.number}</th>
            <th scope="col" className="col-opt">
              {sv.entry.openedOn}
            </th>
            <th scope="col">{sv.entry.jobDescription}</th>
            <th scope="col" className="r col-opt">
              {sv.entry.hours}
            </th>
            <th scope="col" className="r">
              {sv.billing.total}
            </th>
            <th scope="col">{sv.columns.status}</th>
            {canWrite ? <th scope="col">{sv.columns.action}</th> : null}
          </tr>
        </thead>
        <tbody>
          {jobs.data?.map((j) => (
            <tr key={j.id}>
              <td className="num">{j.number}</td>
              <td className="col-opt num">{j.openedOn}</td>
              <td>{j.description}</td>
              <td className="r col-opt">{j.hours}</td>
              <td className="r">{kr(j.totalSek)}</td>
              <td>
                <Status tone={j.status === 'oppen' ? 'active' : 'done'}>
                  {sv.entry.jobStatus[j.status] ?? j.status}
                </Status>
              </td>
              {canWrite ? (
                <td>
                  {j.status === 'oppen' ? (
                    <button
                      className="btn btn-quiet"
                      type="button"
                      aria-label={`${sv.entry.closeJob} ${j.number}`}
                      onClick={() => {
                        setClosing(j);
                        setCloseHours(String(j.hours));
                        setCloseParts(String(j.partsSek));
                      }}
                    >
                      {sv.entry.closeJob}
                    </button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </Table>
      {closing ? (
        <form
          className="form-block reveal"
          aria-labelledby="close-job"
          onSubmit={(e) => {
            e.preventDefault();
            close.mutate();
          }}
        >
          <h3 className="form-heading" id="close-job">
            {sv.entry.closeJob} {closing.number}
          </h3>
          <TextField
            id="closeHours"
            label={sv.entry.hours}
            type="number"
            value={closeHours}
            onChange={setCloseHours}
          />
          <TextField
            id="closeParts"
            label={sv.entry.parts}
            type="number"
            value={closeParts}
            onChange={setCloseParts}
          />
          <p className="muted">
            {sv.entry.jobTotal(
              kr((num(closeHours) ?? 0) * closing.hourlyRateSek + (num(closeParts) ?? 0)),
            )}
          </p>
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={close.isPending}>
              {sv.entry.save}
            </button>
            <button className="btn" type="button" onClick={() => setClosing(null)}>
              {sv.entry.cancel}
            </button>
          </div>
        </form>
      ) : null}
      {canWrite && !open ? (
        <p className="actions-inline">
          <button className="btn" type="button" onClick={() => setOpen(true)}>
            {sv.entry.addWorkshop}
          </button>
        </p>
      ) : null}
      {open ? (
        <form
          className="form-block reveal"
          aria-labelledby="job-form"
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate({
              openedOn: d.openedOn,
              description: d.description,
              hours: num(d.hours) ?? 0,
              hourlyRateSek: num(d.hourlyRateSek) ?? 0,
              partsSek: num(d.partsSek) ?? 0,
              mechanic: d.mechanic || null,
            });
          }}
        >
          <h3 className="form-heading" id="job-form">
            {sv.entry.addWorkshop}
          </h3>
          <ErrorSummary errors={Object.entries(errors).map(([k, v]) => ({ id: k, message: v }))} />
          <TextField
            id="openedOn"
            label={sv.entry.openedOn}
            type="date"
            value={d.openedOn}
            required
            onChange={set('openedOn')}
          />
          <TextField
            id="jobDescription"
            label={sv.entry.jobDescription}
            value={d.description}
            required
            error={errors.description}
            onChange={set('description')}
          />
          <TextField
            id="hours"
            label={sv.entry.hours}
            type="number"
            value={d.hours}
            onChange={set('hours')}
          />
          <TextField
            id="hourlyRateSek"
            label={sv.entry.hourlyRate}
            type="number"
            value={d.hourlyRateSek}
            onChange={set('hourlyRateSek')}
          />
          <TextField
            id="partsSek"
            label={sv.entry.parts}
            type="number"
            value={d.partsSek}
            onChange={set('partsSek')}
          />
          <TextField
            id="mechanic"
            label={sv.entry.mechanic}
            value={d.mechanic}
            onChange={set('mechanic')}
          />
          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={m.isPending || !d.description.trim()}
            >
              {sv.entry.save}
            </button>
            <button className="btn" type="button" onClick={() => setOpen(false)}>
              {sv.entry.cancel}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
